/**
 * FreshserviceIntegrationCard — Settings card that lets a tenant admin
 * enable Freshservice ticket creation on package creation.
 *
 * When enabled, ALICE POSTs a ticket to
 * https://{domain}/api/v2/tickets every time a package completes (new or
 * auto-update). The API key is read from the backend environment
 * (FRESHSERVICE_API_KEY) — it is never stored per tenant. Requester: app
 * owner's email from governance, falling back to the default requester
 * configured here.
 *
 * Custom fields let a tenant satisfy mandatory Freshservice ticket-form
 * fields (e.g. vendor, software_name, complexity). Blank software_name /
 * vendor are auto-filled from the package. A "Send test ticket" button
 * creates a real ticket and surfaces the exact Freshservice response.
 *
 * Backend contract:
 *   GET  /api/tenant/settings/freshservice
 *   PUT  /api/tenant/settings/freshservice
 *   POST /api/tenant/settings/freshservice/test
 * Body: { enabled, domain, default_requester_email, priority, custom_fields }
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, Ticket, Plus, Trash2, Send, CheckCircle2 } from "lucide-react";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const PRIORITIES = [
  { value: "1", label: "1 — Low" },
  { value: "2", label: "2 — Medium" },
  { value: "3", label: "3 — High" },
  { value: "4", label: "4 — Urgent" },
];

const objToRows = (obj) =>
  obj && typeof obj === "object"
    ? Object.entries(obj).map(([key, value]) => ({ key, value: value ?? "" }))
    : [];

const rowsToObj = (rows) => {
  const out = {};
  rows.forEach(({ key, value }) => {
    const k = (key || "").trim();
    if (k) out[k] = value ?? "";
  });
  return out;
};

export default function FreshserviceIntegrationCard({ apiBase, authHeaders }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [domain, setDomain] = useState("");
  const [requester, setRequester] = useState("");
  const [priority, setPriority] = useState("2");
  const [ticketType, setTicketType] = useState("");
  const [category, setCategory] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [statusAutomation, setStatusAutomation] = useState(true);
  const [customRows, setCustomRows] = useState([]);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeaders();
        const r = await axios.get(
          `${apiBase}/tenant/settings/freshservice`,
          { headers },
        );
        setEnabled(!!r.data?.enabled);
        setDomain(r.data?.domain || "");
        setRequester(r.data?.default_requester_email || "");
        setPriority(String(r.data?.priority || 2));
        setTicketType(r.data?.ticket_type || "");
        setCategory(r.data?.category || "");
        setGroupName(r.data?.group_name || "");
        setGroupId(r.data?.group_id != null ? String(r.data.group_id) : "");
        setCompanyName(r.data?.company_name || "");
        setDepartmentId(r.data?.department_id != null ? String(r.data.department_id) : "");
        setStatusAutomation(r.data?.status_automation !== false);
        // Pre-seed the ALICE default custom fields so tickets work out of the box.
        setCustomRows(
          r.data?.custom_fields && Object.keys(r.data.custom_fields).length
            ? objToRows(r.data.custom_fields)
            : [
                { key: "vendor", value: "" },
                { key: "software_name", value: "" },
                { key: "software_version", value: "" },
                { key: "complexity", value: "Medium" },
              ],
        );
        setApiKeyConfigured(!!r.data?.api_key_configured);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, authHeaders]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSavedAt(null);
    try {
      const headers = await authHeaders();
      const r = await axios.put(
        `${apiBase}/tenant/settings/freshservice`,
        {
          enabled,
          domain: domain || null,
          default_requester_email: requester || null,
          priority: parseInt(priority, 10),
          ticket_type: ticketType || null,
          category: category || null,
          group_name: groupName || null,
          group_id: groupId ? parseInt(groupId, 10) : null,
          company_name: companyName || null,
          department_id: departmentId ? parseInt(departmentId, 10) : null,
          status_automation: statusAutomation,
          custom_fields: rowsToObj(customRows),
        },
        { headers },
      );
      setDomain(r.data?.domain || "");
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      // Persist current settings first so the test uses what's on screen.
      await save();
      const headers = await authHeaders();
      const r = await axios.post(
        `${apiBase}/tenant/settings/freshservice/test`,
        {},
        { headers },
      );
      setTestResult(r.data);
    } catch (e) {
      setTestResult({
        ok: false,
        error: e?.response?.data?.detail || e.message || "Test failed",
      });
    } finally {
      setTesting(false);
    }
  };

  const addRow = () => setCustomRows((rows) => [...rows, { key: "", value: "" }]);
  const updateRow = (i, field, val) =>
    setCustomRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeRow = (i) =>
    setCustomRows((rows) => rows.filter((_, idx) => idx !== i));

  return (
    <Card data-testid="freshservice-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <Ticket className="h-5 w-5" />
          Freshservice Integration
        </CardTitle>
        <CardDescription>
          When enabled, ALICE creates a Freshservice ticket every time a
          package is created (new or via the auto-update scheduler). Runs
          alongside the request emails — both can be on at once. Subject:&nbsp;
          <code>ALICE - [Tenant] - [App] [Version]</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Enable Freshservice tickets</Label>
                <p className="text-xs text-white/60 mt-1">
                  Off by default. Turn on to auto-create a ticket on every
                  completed package.
                </p>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="freshservice-toggle"
              />
            </div>

            <p
              className={`text-xs ${apiKeyConfigured ? "text-emerald-400" : "text-amber-400"}`}
              data-testid="freshservice-api-key-status"
            >
              {apiKeyConfigured
                ? "API key detected on the backend (FRESHSERVICE_API_KEY)."
                : "No API key set — add FRESHSERVICE_API_KEY to the backend .env for tickets to be created."}
            </p>

            <div className="grid gap-4">
              <div>
                <Label htmlFor="fs-domain" className="text-sm">
                  Freshservice domain <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="fs-domain"
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="yourcompany.freshservice.com"
                  disabled={!enabled}
                  data-testid="freshservice-domain"
                  className="mt-1"
                />
                <p className="text-xs text-white/50 mt-1">
                  Use your canonical helpdesk domain
                  &nbsp;<code>yourcompany.freshservice.com</code>&nbsp;(or just
                  &apos;yourcompany&apos;). A vanity/portal URL such as
                  &nbsp;<code>support.yourcompany.com</code>&nbsp;often does
                  <strong>&nbsp;not</strong>&nbsp;serve the ticket API — if the
                  test below returns 404, switch to the
                  &nbsp;<code>.freshservice.com</code>&nbsp;domain.
                </p>
              </div>

              <div>
                <Label htmlFor="fs-requester" className="text-sm">
                  Default requester email <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="fs-requester"
                  type="email"
                  value={requester}
                  onChange={(e) => setRequester(e.target.value)}
                  placeholder="alice@yourmsp.com"
                  disabled={!enabled}
                  data-testid="freshservice-requester"
                  className="mt-1"
                />
                <p className="text-xs text-white/50 mt-1">
                  Tickets are raised as the app owner&apos;s email from
                  governance; this address is used when an app has no owner
                  email set.
                </p>
              </div>

              <div>
                <Label className="text-sm">Ticket priority</Label>
                <Select
                  value={priority}
                  onValueChange={setPriority}
                  disabled={!enabled}
                >
                  <SelectTrigger className="mt-1" data-testid="freshservice-priority">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ticket defaults — auto-populated on every ALICE ticket */}
              <div className="grid gap-4 rounded-lg border border-white/10 p-4">
                <p className="text-sm font-medium text-white/80">
                  Ticket defaults
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="fs-type" className="text-sm">Type</Label>
                    <Input
                      id="fs-type"
                      value={ticketType}
                      onChange={(e) => setTicketType(e.target.value)}
                      placeholder="Service Request"
                      disabled={!enabled}
                      data-testid="freshservice-type"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fs-category" className="text-sm">Category</Label>
                    <Input
                      id="fs-category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      placeholder="ALICE"
                      disabled={!enabled}
                      data-testid="freshservice-category"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fs-group" className="text-sm">Group</Label>
                    <Input
                      id="fs-group"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      placeholder="ALICE Team"
                      disabled={!enabled}
                      data-testid="freshservice-group"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fs-group-id" className="text-sm">
                      Group ID <span className="text-white/40">(optional)</span>
                    </Label>
                    <Input
                      id="fs-group-id"
                      value={groupId}
                      onChange={(e) => setGroupId(e.target.value.replace(/[^0-9]/g, ""))}
                      placeholder="e.g. 21000012345"
                      disabled={!enabled}
                      data-testid="freshservice-group-id"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fs-company" className="text-sm">Company (Department)</Label>
                    <Input
                      id="fs-company"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Defaults to the tenant name"
                      disabled={!enabled}
                      data-testid="freshservice-company"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fs-dept-id" className="text-sm">
                      Department ID <span className="text-white/40">(optional)</span>
                    </Label>
                    <Input
                      id="fs-dept-id"
                      value={departmentId}
                      onChange={(e) => setDepartmentId(e.target.value.replace(/[^0-9]/g, ""))}
                      placeholder="e.g. 21000067890"
                      disabled={!enabled}
                      data-testid="freshservice-department-id"
                      className="mt-1"
                    />
                  </div>
                </div>
                <p className="text-xs text-white/50">
                  Type / Category and the sub-category (New Application vs Update
                  Application) are set automatically. <strong>Group</strong> and
                  {" "}<strong>Company</strong> are matched to Freshservice by name;
                  if your API key can&apos;t list them (the test shows them
                  unassigned), paste the numeric <strong>Group ID</strong> /
                  {" "}<strong>Department ID</strong> from the Freshservice admin.
                </p>
              </div>

              {/* Lifecycle status automation */}
              <div className="rounded-lg border border-white/10 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Auto-update status through the lifecycle</Label>
                    <p className="text-xs text-white/60 mt-1">
                      Updates the <strong>same</strong> ticket as the package
                      moves through ALICE — no new tickets are created.
                    </p>
                  </div>
                  <Switch
                    checked={statusAutomation}
                    onCheckedChange={setStatusAutomation}
                    disabled={!enabled}
                    data-testid="freshservice-status-automation"
                  />
                </div>
                <div className="text-xs text-white/50 grid gap-1">
                  <span>• Packaging starts → <strong>Packaging</strong> (stays here if the build fails, with a failure note)</span>
                  <span>• Package built successfully → <strong>QA</strong></span>
                  <span>• Deployed to test → <strong>UAT</strong></span>
                  <span>• Live rollout 10% &amp; 50% → <strong>Release</strong> (+ Intune install-figure note)</span>
                  <span>• Live rollout 100% → <strong>Resolved</strong> (+ final install-figure note)</span>
                </div>
                <div className="text-xs text-amber-300/80 border-t border-white/10 pt-2 mt-1">
                  A related <strong>Incident</strong> (child of the request ticket) is also raised
                  automatically when: a build <strong>fails</strong>, a tester <strong>rejects</strong> UAT,
                  or a live deployment&apos;s <strong>install failures exceed 10%</strong> of a phase&apos;s
                  devices — checked periodically (~5&nbsp;min), counting failures only (pending devices
                  excluded), and raised once per phase.
                </div>
              </div>

              {/* Custom fields — required by some Freshservice ticket forms */}
              <div>
                <Label className="text-sm">Custom fields</Label>
                <p className="text-xs text-white/50 mt-1 mb-2">
                  Add any fields your Freshservice ticket form marks as
                  mandatory (e.g. <code>vendor</code>, <code>software_name</code>,
                  {" "}<code>complexity</code>). Leave <code>software_name</code>{" "}
                  or <code>vendor</code> blank to auto-fill them from the package.
                  If a test ticket fails with a &quot;missing_field&quot; error,
                  add that field name here.
                </p>
                <div className="space-y-2" data-testid="freshservice-custom-fields">
                  {customRows.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={row.key}
                        onChange={(e) => updateRow(i, "key", e.target.value)}
                        placeholder="field name (e.g. complexity)"
                        disabled={!enabled}
                        data-testid={`freshservice-cf-key-${i}`}
                        className="flex-1"
                      />
                      <Input
                        value={row.value}
                        onChange={(e) => updateRow(i, "value", e.target.value)}
                        placeholder="value (blank = auto)"
                        disabled={!enabled}
                        data-testid={`freshservice-cf-value-${i}`}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRow(i)}
                        disabled={!enabled}
                        data-testid={`freshservice-cf-remove-${i}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addRow}
                    disabled={!enabled}
                    data-testid="freshservice-cf-add"
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" /> Add custom field
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {savedAt && !error && (
              <p className="text-xs text-emerald-400">
                Saved {savedAt.toLocaleTimeString()}.
              </p>
            )}

            {testResult && (
              <Alert
                variant={testResult.ok ? "default" : "destructive"}
                data-testid="freshservice-test-result"
              >
                <AlertTitle className="flex items-center gap-2">
                  {testResult.ok ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" /> Test ticket created
                    </>
                  ) : (
                    "Test ticket failed"
                  )}
                </AlertTitle>
                <AlertDescription>
                  {testResult.ok ? (
                    <span>
                      Freshservice ticket #{testResult.ticket_id} was created
                      successfully.
                      {testResult.portal_url && (
                        <>
                          {" "}
                          <a
                            href={testResult.portal_url}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                          >
                            Open in Freshservice
                          </a>
                        </>
                      )}
                    </span>
                  ) : (
                    testResult.error || "Unknown error"
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={sendTest}
                disabled={testing || saving || !enabled}
                data-testid="freshservice-test"
                className="flex items-center gap-2"
              >
                {testing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send test ticket
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid="freshservice-save"
                className="flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save changes
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
